import React, { useMemo, useRef, useState } from 'react'
import { Form } from '../../../lib/ui/form'
import { Separator } from '../../../lib/ui/separator'
import { useProgramSurveyForm } from './useProgramSurveyForm'
import { Button } from '~/lib/ui/button'
import { RatingField } from '../RatingField'
import { TextareaField } from '../TextareaField'

export default function FirstWeekSurveyForm({
  questions,
  choices,
  candidateId,
  companyId,
  setError,
  setThankYou,
}) {
  const { form, onSubmit } = useProgramSurveyForm({
    surveyType: 'program_survey_first_week',
    questions,
    candidate_id: candidateId,
    company_id: companyId,
    setError,
    setThankYou,
  })

  const [showCoachRating, setShowCoachRating] = useState(false)
  const [coachRatingAnnouncement, setCoachRatingAnnouncement] = useState('')
  const coachRatingContainerRef = useRef(null)

  const coachRatingQuestionName = useMemo(() => {
    const dependentQuestion = Object.values(questions).find(
      (question) =>
        question.type === 'star' &&
        question?.dependency?.question === 'interacted_with_coach',
    )

    return dependentQuestion?.name
  }, [questions])

  React.useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (
        name === questions.interacted_with_coach.name &&
        value[questions.interacted_with_coach.name] === true
      ) {
        setShowCoachRating(true)
        setCoachRatingAnnouncement(
          'Additional coaching rating question is now visible.',
        )
      } else if (
        name === questions.interacted_with_coach.name &&
        value[questions.interacted_with_coach.name] === false
      ) {
        setShowCoachRating(false)
        setCoachRatingAnnouncement(
          'Additional coaching rating question hidden.',
        )
      }
    })
    return () => subscription.unsubscribe()
  }, [form, questions.interacted_with_coach.name])

  React.useEffect(() => {
    if (!showCoachRating || !coachRatingContainerRef.current) return

    // Move focus to the revealed field group so keyboard and screen reader users are notified of context change.
    coachRatingContainerRef.current.focus()
  }, [showCoachRating, coachRatingQuestionName])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="sr-only" aria-live="polite" role="status">
          {coachRatingAnnouncement}
        </div>
        {Object.entries(questions).map(([key, question]) => {
          if (question.type === 'star') {
            if (question?.dependency?.question === 'interacted_with_coach') {
              if (showCoachRating) {
                return (
                  <div
                    className="my-6 rounded-xl bg-white p-6"
                    key={question.name}
                    ref={coachRatingContainerRef}
                    tabIndex={-1}
                  >
                    <RatingField
                      name={question.name}
                      label={question.name}
                      choices={choices[key]}
                      control={form.control}
                      programSurvey={true}
                      star={true}
                    />
                  </div>
                )
              }
            } else {
              return (
                <div
                  className="my-6 rounded-xl bg-white p-6"
                  key={question.name}
                >
                  <RatingField
                    name={question.name}
                    label={question.name}
                    choices={choices[key]}
                    control={form.control}
                    programSurvey={true}
                    star={true}
                  />
                </div>
              )
            }
          } else if (question.type === 'boolean') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <RatingField
                  name={question.name}
                  label={question.name}
                  choices={choices[key]}
                  control={form.control}
                  yesOrNo={true}
                  programSurvey={true}
                />
              </div>
            )
          } else if (question.type === 'text') {
            return (
              <div className="my-6 rounded-xl bg-white p-6" key={question.name}>
                <TextareaField
                  name={question.name}
                  label={question.name}
                  control={form.control}
                  programSurvey={true}
                />
              </div>
            )
          }
        })}
        <Separator className="my-8" />

        <div className="flex w-full justify-center">
          <Button type="submit">Submit</Button>
        </div>
      </form>
    </Form>
  )
}
